import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Minus, Users, Building2, Briefcase, Loader2, Save,
  Euro, Grape, Beef, Carrot, Apple, Wheat, Flower2, Factory,
  Tractor, Leaf, Store, Package, ChevronRight, Cog, Landmark,
  Flame, Zap, Sun, Scissors, PlusCircle, CroissantIcon,
} from 'lucide-react';
import api from '@/lib/api';

const PACK_ICONS = {
  viticulture: Grape, elevage_bovin: Beef, elevage_ovin: Beef,
  maraichage: Carrot, arboriculture: Apple, grandes_cultures: Wheat,
  ppam: Flower2, transformation: Factory, eta: Tractor,
  equitation: Briefcase, bio: Leaf, expediteur: Package,
  vente_directe: Store, cuma: Cog, syndicat_agricole: Landmark,
  methanisation: Flame, pyrogazeification: Zap, energie_solaire: Sun,
  fromagerie: Factory, brasserie: Factory, decoupe_ferme: Scissors,
  boulangerie_ferme: Store, autre: PlusCircle,
};

const PALIERS = {
  petite: { nom: 'Petite Exploitation', prix: '100-150', defaut: 125, color: 'border-blue-400 bg-blue-50', accent: 'text-blue-700' },
  moyenne: { nom: 'Moyenne Exploitation', prix: '500', defaut: 500, color: 'border-amber-400 bg-amber-50', accent: 'text-amber-700' },
  grosse: { nom: 'Grosse Exploitation', prix: '1200', defaut: 1200, color: 'border-emerald-400 bg-emerald-50', accent: 'text-emerald-700' },
};

const SOURCES = {
  direct: 'Contact direct', ambassadeur: 'Ambassadeur / Parrain',
  bouche_a_oreille: 'Bouche à oreille', salon: 'Salon / Événement', web: 'Site web',
};

const emptyCollaborateur = () => ({ nom: '', prenom: '', email: '', role: 'utilisateur' });
const emptyWorkspace = () => ({ nom: '', type: 'exploitation_agricole' });

export default function ConfigurateurProspect({ open, onOpenChange, onSuccess, mode = 'modal' }) {
  const [packs, setPacks] = useState({});
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [saving, setSaving] = useState(false);
  const [devisResult, setDevisResult] = useState(null);
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    nom_entreprise: '', contact_nom: '', contact_prenom: '', contact_email: '',
    contact_telephone: '', ville: '', surface_ha: '', palier: 'petite',
    source: 'direct', ambassadeur_email: '', ambassadeur_nom: '', notes: '',
    type_paiement: 'mensuel',
  });

  const [collaborateurs, setCollaborateurs] = useState([emptyCollaborateur()]);
  const [workspaces, setWorkspaces] = useState([emptyWorkspace()]);
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [autreDescription, setAutreDescription] = useState('');

  useEffect(() => {
    api.get('/crm-vente-logiciel/packs-metiers')
      .then(res => setPacks(res.data?.packs || {}))
      .catch(() => {})
      .finally(() => setLoadingPacks(false));
  }, []);

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addCollaborateur = () => setCollaborateurs(prev => [...prev, emptyCollaborateur()]);
  const removeCollaborateur = (i) => setCollaborateurs(prev => prev.filter((_, idx) => idx !== i));
  const updateCollaborateur = (i, key, val) => setCollaborateurs(prev => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));

  const addWorkspace = () => setWorkspaces(prev => [...prev, emptyWorkspace()]);
  const removeWorkspace = (i) => setWorkspaces(prev => prev.filter((_, idx) => idx !== i));
  const updateWorkspace = (i, key, val) => setWorkspaces(prev => prev.map((w, idx) => idx === i ? { ...w, [key]: val } : w));

  const togglePack = (packId) => {
    setSelectedPacks(prev => prev.includes(packId) ? prev.filter(p => p !== packId) : [...prev, packId]);
  };

  const prixEstime = useMemo(() => {
    const base = PALIERS[form.palier]?.defaut || 125;
    const packsSupp = selectedPacks.reduce((acc, p) => acc + (packs[p]?.prix_supplementaire || 0), 0);
    const collabSupp = Math.max(0, collaborateurs.length - 1) * 29;
    const wsSupp = workspaces.length > 1 ? [0, 0, 49, 89, 119, 149][Math.min(workspaces.length, 5)] || 179 : 0;
    return base + packsSupp + collabSupp + wsSupp;
  }, [form.palier, selectedPacks, collaborateurs.length, workspaces.length, packs]);

  const handleSubmit = async () => {
    if (!form.nom_entreprise || !form.contact_email) {
      toast.error('Nom entreprise et email sont requis');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/crm-vente-logiciel/creer-prospect', {
        ...form,
        surface_ha: parseInt(form.surface_ha) || 0,
        activites: selectedPacks,
        activite_autre_description: autreDescription || undefined,
        nb_workspaces: workspaces.length,
        nb_collaborateurs: collaborateurs.length,
        options: [],
        prix_mensuel_propose: prixEstime,
        demande_speciale: JSON.stringify({
          collaborateurs: collaborateurs.filter(c => c.nom || c.email),
          workspaces: workspaces.filter(w => w.nom),
        }),
      });
      if (res.data?.success) {
        toast.success('Prospect configuré et synchronisé CRM/Compta !');
        setDevisResult(res.data.devis);
        if (onSuccess) onSuccess();
        if (mode === 'modal') {
          resetForm();
          onOpenChange(false);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({ nom_entreprise: '', contact_nom: '', contact_prenom: '', contact_email: '',
      contact_telephone: '', ville: '', surface_ha: '', palier: 'petite',
      source: 'direct', ambassadeur_email: '', ambassadeur_nom: '', notes: '', type_paiement: 'mensuel' });
    setCollaborateurs([emptyCollaborateur()]);
    setWorkspaces([emptyWorkspace()]);
    setSelectedPacks([]);
    setAutreDescription('');
    setDevisResult(null);
    setStep(1);
  };

  const content = (
    <div className="space-y-6" data-testid="configurateur-prospect">
      {/* Steps Indicator */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { n: 1, label: 'Entreprise' },
          { n: 2, label: 'Collaborateurs & Workspaces' },
          { n: 3, label: 'Fiches Métiers' },
          { n: 4, label: 'Récapitulatif' },
        ].map((s, i) => (
          <div key={s.n} className="flex items-center gap-2">
            <button
              onClick={() => setStep(s.n)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                step === s.n ? 'bg-emerald-600 text-white' : step > s.n ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}
              data-testid={`step-${s.n}`}
            >
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-[10px] font-bold">
                {step > s.n ? '✓' : s.n}
              </span>
              {s.label}
            </button>
            {i < 3 && <ChevronRight className="w-3 h-3 text-gray-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Entreprise */}
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <Label className="text-sm font-semibold mb-3 block">Palier tarifaire</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(PALIERS).map(([key, p]) => (
                <div
                  key={key}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all text-center ${
                    form.palier === key ? `${p.color} ring-2 ring-offset-1` : 'border-muted hover:border-muted-foreground/50'
                  }`}
                  onClick={() => updateForm('palier', key)}
                  data-testid={`config-palier-${key}`}
                >
                  <p className={`font-bold text-lg ${p.accent}`}>{p.prix}€</p>
                  <p className="text-xs text-muted-foreground">/mois</p>
                  <p className="font-medium text-xs mt-1">{p.nom}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Nom de l'entreprise *</Label>
              <Input value={form.nom_entreprise} onChange={e => updateForm('nom_entreprise', e.target.value)} placeholder="Ex: EARL Les Vignes" className="mt-1" data-testid="config-nom-entreprise" />
            </div>
            <div><Label>Prénom</Label><Input value={form.contact_prenom} onChange={e => updateForm('contact_prenom', e.target.value)} placeholder="Jean" className="mt-1" /></div>
            <div><Label>Nom</Label><Input value={form.contact_nom} onChange={e => updateForm('contact_nom', e.target.value)} placeholder="Dupont" className="mt-1" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.contact_email} onChange={e => updateForm('contact_email', e.target.value)} placeholder="contact@ferme.fr" className="mt-1" data-testid="config-email" /></div>
            <div><Label>Téléphone</Label><Input value={form.contact_telephone} onChange={e => updateForm('contact_telephone', e.target.value)} placeholder="06 12 34 56 78" className="mt-1" /></div>
            <div><Label>Ville</Label><Input value={form.ville} onChange={e => updateForm('ville', e.target.value)} placeholder="Lyon" className="mt-1" /></div>
            <div><Label>Surface (ha)</Label><Input type="number" value={form.surface_ha} onChange={e => updateForm('surface_ha', e.target.value)} placeholder="50" className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={v => updateForm('source', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type de paiement</Label>
              <Select value={form.type_paiement} onValueChange={v => updateForm('type_paiement', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="annuel">Annuel (-15%)</SelectItem>
                  <SelectItem value="2ans">Engagement 2 ans (-20%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.source === 'ambassadeur' && (
              <>
                <div><Label>Nom ambassadeur</Label><Input value={form.ambassadeur_nom} onChange={e => updateForm('ambassadeur_nom', e.target.value)} className="mt-1" /></div>
                <div><Label>Email ambassadeur</Label><Input value={form.ambassadeur_email} onChange={e => updateForm('ambassadeur_email', e.target.value)} className="mt-1" /></div>
              </>
            )}
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => updateForm('notes', e.target.value)} placeholder="Informations complémentaires..." className="mt-1" rows={2} />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} data-testid="config-next-1">Suivant <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Step 2: Collaborateurs & Workspaces */}
      {step === 2 && (
        <div className="space-y-6">
          {/* Collaborateurs */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4" />Collaborateurs ({collaborateurs.length})</Label>
              <Button variant="outline" size="sm" onClick={addCollaborateur} data-testid="add-collaborateur"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
            </div>
            <div className="space-y-3">
              {collaborateurs.map((c, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg border bg-gray-50/50" data-testid={`collaborateur-${i}`}>
                  <div className="grid grid-cols-4 gap-2 flex-1">
                    <Input placeholder="Prénom" value={c.prenom} onChange={e => updateCollaborateur(i, 'prenom', e.target.value)} className="text-sm" />
                    <Input placeholder="Nom" value={c.nom} onChange={e => updateCollaborateur(i, 'nom', e.target.value)} className="text-sm" />
                    <Input placeholder="Email" value={c.email} onChange={e => updateCollaborateur(i, 'email', e.target.value)} className="text-sm" />
                    <Select value={c.role} onValueChange={v => updateCollaborateur(i, 'role', v)}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="utilisateur">Utilisateur</SelectItem>
                        <SelectItem value="lecture_seule">Lecture seule</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {collaborateurs.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeCollaborateur(i)}><Minus className="w-3 h-3 text-red-500" /></Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Workspaces */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" />Workspaces / Sociétés ({workspaces.length})</Label>
              <Button variant="outline" size="sm" onClick={addWorkspace} data-testid="add-workspace"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
            </div>
            <div className="space-y-3">
              {workspaces.map((w, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg border bg-gray-50/50" data-testid={`workspace-${i}`}>
                  <div className="grid grid-cols-2 gap-2 flex-1">
                    <Input placeholder="Nom du workspace" value={w.nom} onChange={e => updateWorkspace(i, 'nom', e.target.value)} className="text-sm" />
                    <Select value={w.type} onValueChange={v => updateWorkspace(i, 'type', v)}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exploitation_agricole">Exploitation agricole</SelectItem>
                        <SelectItem value="earl">EARL</SelectItem>
                        <SelectItem value="gaec">GAEC</SelectItem>
                        <SelectItem value="scea">SCEA</SelectItem>
                        <SelectItem value="sci">SCI</SelectItem>
                        <SelectItem value="sarl">SARL</SelectItem>
                        <SelectItem value="individuel">Individuel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {workspaces.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeWorkspace(i)}><Minus className="w-3 h-3 text-red-500" /></Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>Retour</Button>
            <Button onClick={() => setStep(3)} data-testid="config-next-2">Suivant <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Step 3: Fiches Métiers */}
      {step === 3 && (
        <div className="space-y-4">
          <Label className="text-sm font-semibold flex items-center gap-2"><Briefcase className="w-4 h-4" />Packs Métiers ({selectedPacks.length} sélectionnés)</Label>
          {loadingPacks ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="grid grid-cols-2 gap-3 pr-3">
                {Object.entries(packs).map(([id, pack]) => {
                  const Icon = PACK_ICONS[id] || Briefcase;
                  const isSelected = selectedPacks.includes(id);
                  return (
                    <div
                      key={id}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => togglePack(id)}
                      data-testid={`pack-${id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded" style={{ backgroundColor: pack.couleur + '20' }}>
                          <Icon className="w-4 h-4" style={{ color: pack.couleur }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm">{pack.nom}</p>
                            <Checkbox checked={isSelected} className="ml-2" />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{pack.description}</p>
                          <Badge variant="outline" className="text-[10px] mt-1.5">+{pack.prix_supplementaire}€/mois</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
          {selectedPacks.includes('autre') && (
            <div className="mt-2">
              <Label className="text-sm font-semibold">Décrivez votre activité</Label>
              <Input
                value={autreDescription}
                onChange={e => setAutreDescription(e.target.value)}
                placeholder="Ex: Apiculture, Héliciculture, Pisciculture..."
                className="mt-1"
                data-testid="autre-description-input"
              />
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>Retour</Button>
            <Button onClick={() => setStep(4)} data-testid="config-next-3">Récapitulatif <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Step 4: Récapitulatif */}
      {step === 4 && (
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg text-emerald-800">Estimation mensuelle</h3>
                <p className="text-3xl font-bold text-emerald-700">{prixEstime}€<span className="text-sm font-normal">/mois</span></p>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Base {PALIERS[form.palier]?.nom}</span><span>{PALIERS[form.palier]?.defaut}€</span></div>
                {selectedPacks.length > 0 && <div className="flex justify-between"><span>{selectedPacks.length} pack(s) métier</span><span>+{selectedPacks.reduce((a, p) => a + (packs[p]?.prix_supplementaire || 0), 0)}€</span></div>}
                {collaborateurs.length > 1 && <div className="flex justify-between"><span>{collaborateurs.length - 1} collaborateur(s) supp.</span><span>+{(collaborateurs.length - 1) * 29}€</span></div>}
                {workspaces.length > 1 && <div className="flex justify-between"><span>{workspaces.length} workspace(s)</span><span>+{[0, 0, 49, 89, 119, 149][Math.min(workspaces.length, 5)] || 179}€</span></div>}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 rounded-lg border">
              <Label className="text-xs text-muted-foreground">Entreprise</Label>
              <p className="font-medium">{form.nom_entreprise || '-'}</p>
              <p className="text-muted-foreground">{form.contact_prenom} {form.contact_nom}</p>
              <p className="text-muted-foreground">{form.contact_email}</p>
            </div>
            <div className="p-3 rounded-lg border">
              <Label className="text-xs text-muted-foreground">Configuration</Label>
              <p>{collaborateurs.length} collaborateur(s)</p>
              <p>{workspaces.length} workspace(s)</p>
              <p>{selectedPacks.length} fiche(s) métier</p>
            </div>
          </div>

          {selectedPacks.length > 0 && (
            <div className="p-3 rounded-lg border">
              <Label className="text-xs text-muted-foreground mb-2 block">Packs Métiers sélectionnés</Label>
              <div className="flex flex-wrap gap-1.5">
                {selectedPacks.map(p => (
                  <Badge key={p} style={{ backgroundColor: packs[p]?.couleur + '20', color: packs[p]?.couleur, borderColor: packs[p]?.couleur }}>
                    {packs[p]?.nom}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(3)}>Retour</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="config-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Créer & Synchroniser CRM/Compta
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (mode === 'tab') return content;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" />
            Configurateur Sur-Mesure
          </DialogTitle>
          <DialogDescription>Configurez l'offre complète pour ce prospect</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
